import uuid from 'uuid';

import { ISanityBlockContentChild } from '@rbi-ctg/menu';

import {
  BlockContentItem,
  BlockContentText,
  FormatPlaceholderInterpolationFn,
  PlaceholderFieldsMap,
  isBlockContentText,
} from './types';

export const buildInlineTextBlock = (text: string): ISanityBlockContentChild => ({
  _key: uuid(),
  _type: 'span',
  marks: [],
  text,
});

export const buildBlockContentText = (children: ISanityBlockContentChild[]): BlockContentText => ({
  _key: uuid(),
  _type: 'block',
  markDefs: [],
  style: 'normal',
  children,
});

export const isBlockContentNewLine = (block: BlockContentItem) =>
  isBlockContentText(block) && block.children?.length === 1 && block.children[0].text === '';

/**
 * Transform the LocaleSmartBlockContent blocks with placeholders to formatted texts.
 * Assumes that all texts should be appended inline unless it finds a newLine character.
 */
export const reducePlaceholdersToFormattedTextBlocks = ({
  blocks,
  interpolationSourceObject,
  placeholderFieldsMap,
  formatPlaceholderInterpolation,
}: {
  blocks: BlockContentItem[];
  interpolationSourceObject: Object;
  placeholderFieldsMap: PlaceholderFieldsMap;
  formatPlaceholderInterpolation: FormatPlaceholderInterpolationFn;
}) =>
  blocks.reduce((acc, block) => {
    // All `newLine` characters creates a new block
    if (isBlockContentNewLine(block)) {
      return acc.concat(block as BlockContentText);
    }

    let inlineTextsToAppend: ISanityBlockContentChild[] = [];
    if (isBlockContentText(block)) {
      inlineTextsToAppend = block.children;
    } else {
      const placeholderField = placeholderFieldsMap[block._ref];
      if (placeholderField) {
        const formattedText = formatPlaceholderInterpolation({
          interpolationSourceObject,
          placeholderField: placeholderFieldsMap[block._ref],
        });
        inlineTextsToAppend = [buildInlineTextBlock(formattedText)];
      }
    }

    const lastBlock = acc.length ? acc[acc.length - 1] : null;
    if (lastBlock && !isBlockContentNewLine(lastBlock)) {
      lastBlock.children = [...lastBlock.children, ...inlineTextsToAppend];
    } else {
      acc.push(buildBlockContentText(inlineTextsToAppend));
    }

    return acc;
  }, [] as BlockContentText[]);
